import { useState } from "react";
import StarRatingInput from "../star-rating-input";
import BoldLayout from "../bold-layout";
import { useFeedbackFlow } from "../../hooks/use-feedback-flow";
import type { ApiPage, Brandkit, FormBrand } from "../../types/api.types";

interface StepRatingProps {
	brandkit: Brandkit;
	brand: FormBrand;
	showBackButton?: boolean;
	onBack?: () => void;
	stepKey?: string;
	pageConfig: ApiPage;
	isDarkMode?: boolean;
}

export default function StepRating({
	brandkit,
	brand,
	showBackButton,
	onBack,
	stepKey,
	pageConfig,
	isDarkMode,
}: StepRatingProps) {
	const {
		data,
		setRating,
		nextStep,
		createSubmissionFromRating,
		isSubmitting,
		submitError,
	} = useFeedbackFlow();
	const [error, setError] = useState<string | null>(null);

	const handleRatingSelect = (rating: number) => {
		setRating(rating);
		setError(null); // Clear error when rating changes
	};

	const handleContinue = async () => {
		if (data.rating < 1) return;

		try {
			setError(null);
			await createSubmissionFromRating();
			nextStep();
		} catch (err) {
			setError(
				submitError || "Failed to submit rating. Please try again.",
			);
		}
	};

	const brandLogo =
		brandkit?.logo_icon_with_label_url ||
		brandkit?.logo_icon_only_url ||
		brandkit?.logo_label_only_url ||
		brandkit?.logo_inverted_icon_with_label_url ||
		brandkit?.logo_inverted_icon_only_url ||
		brandkit?.logo_inverted_label_only_url ||
		null;

	return (
		<BoldLayout
			showBranding={brand?.branding !== "disabled"}
			brandLogo={brandLogo}
			brandName={brand.name}
			brandkit={brandkit}
			title={pageConfig.attributes.title}
			description={pageConfig.attributes.description}
			stepKey={stepKey}
			showBackButton={showBackButton}
			onBack={onBack}
			onContinue={handleContinue}
			continueDisabled={data.rating < 1 || isSubmitting}
			continueLabel={isSubmitting ? "Saving..." : "Continue"}
			isDarkMode={isDarkMode}
		>
			<StarRatingInput
				value={data.rating}
				onChange={handleRatingSelect}
				size="large"
				align="left"
			/>
			{error && (
				<div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
					<p className="text-sm text-red-600">{error}</p>
					<button
						onClick={handleContinue}
						className="mt-2 text-sm text-red-700 underline hover:no-underline"
						style={{
							fontFamily: "var(--font-body)",
							fontWeight: "var(--font-body-weight)",
						}}
					>
						Try Again
					</button>
				</div>
			)}
		</BoldLayout>
	);
}
